/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {Component, DestroyRef, Inject, Input, OnInit, signal} from '@angular/core';
import {resourceOptionParams} from '../common/controller/signal-admin-resource-api';
import * as _ from 'lodash';

import template from './copy-targeting-templates.component.html';
import {TargetingHelper} from './targeting-helper';
import {TeamApiService} from '../admin/team/team-api.service';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {targetingTemplateSaveErrors} from './targeting/targeting-template-save.util';
import {
  SignalAdminResourceListState,
  SignalAdminResourceListStateFactory
} from '../common/controller/signal-admin-resource-list-state';
import {TargetingTemplateApiService, TargetingTemplateModel} from './targeting-template-api.service';

import {TARGETING_IMPORTS} from './targeting-imports';
import {TargetingTemplateEditFormComponent} from './targeting/targeting-template-edit-form.component';

@Component({
  imports: [...TARGETING_IMPORTS, TargetingTemplateEditFormComponent],
  selector: 'adn-copy-targeting-templates',
  template
})
export class CopyTargetingTemplatesComponent implements OnInit {
  @Input() tabController: any;

  readonly targetingTemplateList: SignalAdminResourceListState<TargetingTemplateModel>;
  readonly allTeams = signal<any[] | null>(null);
  readonly yourTeams = signal<any[]>([]);
  readonly teams = signal<any[]>([]);
  readonly saving = signal(false);
  readonly saveErrors = signal<string[]>([]);

  constructor(
    private readonly targetingTemplateApi: TargetingTemplateApiService,
    listFactory: SignalAdminResourceListStateFactory,
    destroyRef: DestroyRef,
    @Inject(TeamApiService) private readonly Team: TeamApiService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
  ) {
    this.targetingTemplateList = listFactory.create(
      targetingTemplateApi,
      {
        listId: 'targetingTargetingTemplates',
        additionalParams: {filterBy: 'type', filterByLike: 'TARGETING'},
        syncPageToUrl: false
      },
      destroyRef
    );
  }

  ngOnInit(): void {
    this.targetingTemplateList.load();
  }

  get listReady(): boolean {
    return !this.targetingTemplateList.loading();
  }

  get hasItems(): boolean {
    return this.targetingTemplateList.items().length > 0;
  }

  get displayItems(): TargetingTemplateModel[] {
    return this.targetingTemplateList.items();
  }

  get listPageSize(): number {
    return this.targetingTemplateList.pageSize;
  }

  get listTotalCount(): number {
    return this.targetingTemplateList.totalCount();
  }

  get listCurrentPage(): number {
    return this.targetingTemplateList.currentPage();
  }

  editInvoked(template: any): void {
    this.saveErrors.set([]);
    template.editable = true;
    template.teams = _.map(template.teams, (team) => team.id || team);
    this.teamQuery(template);
  }

  editCancelled(template: any): void {
    template.editable = false;
  }

  editTemplate(template: any): void {
    if (this.saving()) {
      return;
    }

    this.saving.set(true);
    this.saveErrors.set([]);
    const templateForSave = _.cloneDeep(template);
    const nt = templateForSave as TargetingTemplateModel;

    if (!nt.teamType || nt.teamType === 'YOUR') {
      nt.teams = this.yourTeams();
    }

    firstValueFrom(this.targetingTemplateApi.save(nt)).then(() => {
      this.saving.set(false);
      this.reloadList();
    }, (response: any) => {
      this.saving.set(false);
      this.saveErrors.set(targetingTemplateSaveErrors(response));
    });
  }

  deleteInvoked(template: any): void {
    firstValueFrom(this.targetingTemplateApi.setObjectState(template.id, 'INACTIVE')).then(() => {
      this.reloadList();
    });
  }

  copyTemplateInvoked(nt: any): void {
    this.tabController.copyTargetingTemplate(
      TargetingHelper.getTargetingTypeFromTemplate(nt.type).widget,
      nt
    );
    nt.copy = true;
  }

  changePage(page: number, event: Event): void {
    this.targetingTemplateList.changePage(page, event);
  }

  pageStart(): number {
    return this.listPageSize * (this.listCurrentPage - 1) + 1;
  }

  pageEnd(): number {
    return Math.min(this.listPageSize * this.listCurrentPage, this.listTotalCount);
  }

  private reloadList(): void {
    this.targetingTemplateList.load();
  }

  private teamQuery(template: any): void {
    const doType = () => {
      template.teamType = this.yourTeams().length === _.get(template, ['teams', 'length'])
        ? 'YOUR'
        : 'CUSTOM';
    };

    if (this.allTeams()) {
      doType();
      return;
    }

    firstValueFrom(this.Team.listOptions(resourceOptionParams({minimal: true}))).then((page: any) => {
      const allTeams = page.results;
      const isViewAllObjects = this.LocalUserPermissions.hasNetwork('VIEW_ALL_OBJECTS');
      const yourTeams = _.filter(allTeams, (g) => {
        return this.LocalUserPermissions.isPartOf(g.network.tagId || g.network.id, g.id);
      });
      this.allTeams.set(allTeams);
      this.yourTeams.set(yourTeams);
      this.teams.set(isViewAllObjects ? allTeams : yourTeams);
      doType();
    });
  }
}
